import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SortingState, ColumnFiltersState } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import { UserProfile } from '@/app/types/auth';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import {
  ComplianceService,
  ComplianceServiceCreate,
  ComplianceServiceUpdate,
} from '@/app/types/compliance';
import {
  listComplianceServices,
  createComplianceService,
  updateComplianceService,
  deleteComplianceService,
} from '@/app/services/complianceServiceService';
import ComplianceServiceDataTable from './components/ComplianceServiceDataTable';
import ComplianceServiceForm from './components/ComplianceServiceForm';
import { columns } from './components/columns';

// Auth selectors
const selectUser = (state: { user: UserProfile | null }) => state.user;
const selectLogoutAction = (state: { logoutAction: () => void }) => state.logoutAction;
const selectIsLoading = (state: { isLoading: boolean }) => state.isLoading;

const ComplianceServicesPage: React.FC = () => {
  const queryClient = useQueryClient();

  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ComplianceService | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });

  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);

  // Get organization ID from user data
  const userOrgId = useMemo(
    () => user?.organization_id || user?.organization?.id,
    [user]
  );
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);

  // Construct query parameters
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      search_query: searchValue || undefined,
    };

    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? 'desc' : 'asc';
    }

    // Add category filter if present
    const categoryFilter = columnFilters.find((f) => f.id === 'category');
    if (categoryFilter?.value) {
      params.category = (categoryFilter.value as string[])[0];
    }

    return params;
  }, [pagination.pageIndex, pagination.pageSize, searchValue, sorting, columnFilters]);

  // Query to fetch compliance services
  const {
    data: servicesResponse = { items: [], total: 0 },
    isLoading: isServicesLoading,
    isError: isServicesError,
    error: servicesError,
    refetch: refetchServices,
  } = useQuery<PaginatedResponse<ComplianceService>>({
    queryKey: ['compliance-services', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      return listComplianceServices(queryParams);
    },
    enabled: !!userOrgId,
  });

  // Update pagination when total changes
  useEffect(() => {
    if (servicesResponse) {
      setPagination((prev) => ({
        ...prev,
        pageCount: Math.ceil(servicesResponse.total / prev.pageSize),
      }));
    }
  }, [servicesResponse]);

  // Setup event listeners for edit/delete actions from the table
  useEffect(() => {
    const handleEdit = (event: CustomEvent<ComplianceService>): void => {
      setCurrentService(event.detail);
      setIsEditFormOpen(true);
    };

    const handleDelete = (event: CustomEvent<ComplianceService>): void => {
      setCurrentService(event.detail);
      setIsDeleteDialogOpen(true);
    };

    document.addEventListener(
      'edit-compliance-service',
      handleEdit as EventListener
    );
    document.addEventListener(
      'delete-compliance-service',
      handleDelete as EventListener
    );

    return () => {
      document.removeEventListener(
        'edit-compliance-service',
        handleEdit as EventListener
      );
      document.removeEventListener(
        'delete-compliance-service',
        handleDelete as EventListener
      );
    };
  }, []);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: ComplianceServiceCreate) => createComplianceService(data),
    onSuccess: () => {
      toast.success('Compliance service created successfully');
      queryClient.invalidateQueries({ queryKey: ['compliance-services', userOrgId] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(
        `Failed to create service: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ComplianceServiceUpdate }) =>
      updateComplianceService(id, data),
    onSuccess: () => {
      toast.success('Compliance service updated successfully');
      queryClient.invalidateQueries({ queryKey: ['compliance-services', userOrgId] });
      setIsEditFormOpen(false);
      setCurrentService(null);
    },
    onError: (error) => {
      toast.error(
        `Failed to update service: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteComplianceService(id),
    onSuccess: () => {
      toast.success('Compliance service deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['compliance-services', userOrgId] });
      setIsDeleteDialogOpen(false);
      setCurrentService(null);
    },
    onError: (error) => {
      toast.error(
        `Failed to delete service: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  // Event Handlers
  const handleCreateClick = (): void => {
    setIsCreateFormOpen(true);
  };

  const handleCreateSubmit = (formData: ComplianceServiceCreate): void => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: ComplianceServiceUpdate): void => {
    if (currentService) {
      updateMutation.mutate({ id: currentService.id, data: formData });
    }
  };

  const handleDeleteConfirm = (): void => {
    if (currentService) {
      deleteMutation.mutate(currentService.id);
    }
  };

  const handlePaginationChange = (newPagination: {
    pageIndex: number;
    pageSize: number;
  }): void => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPagination.pageIndex,
      pageSize: newPagination.pageSize,
    }));
  };

  // --- Render Logic ---

  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-warning" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to manage compliance services.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }

  if (isServicesError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Data</p>
        <p className="text-sm text-muted-foreground">
          {servicesError instanceof Error
            ? servicesError.message
            : 'An unknown error occurred while loading compliance services.'}
        </p>
        <Button onClick={() => refetchServices()} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Compliance Services</h1>
        {isOrgAdmin && (
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" /> Add Service
          </Button>
        )}
      </div>
      <p className="text-muted-foreground">
        Manage compliance services like GST, Income Tax, TDS, Professional Tax, and
        more. These services can be subscribed to by clients.
      </p>

      {/* Data Table */}
      <ComplianceServiceDataTable
        data={servicesResponse}
        columns={columns}
        isLoading={isServicesLoading}
        pagination={pagination}
        sorting={sorting}
        columnFilters={columnFilters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={setColumnFilters}
      />

      {/* Create Form */}
      <ComplianceServiceForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
      />

      {/* Edit Form */}
      {currentService && (
        <ComplianceServiceForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentService(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentService}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the compliance service &quot;
              {currentService?.name}&quot;. This action cannot be undone. Any
              subscriptions linked to this service may be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ComplianceServicesPage;
